// src/modules/app-views/products/pages/Document/tabs/SuppliersTab/index.jsx
// *****************************************************************************
// SUPPLIERS TAB
// *****************************************************************************

// *****************************************************************************
// IMPORTS
// *****************************************************************************

// 1. React
import { useState, useEffect, useCallback, useMemo } from 'react';

// 2. External libraries
import './suppliers-tab.less';
import { Button, Popconfirm, Tooltip, Typography, Descriptions, Empty, Row, Col, Divider, Tag, Card, Form } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, DollarOutlined } from '@ant-design/icons';
import SimpleCurrency from 'components/SimpleComponents/SimpleCurrency';
import SimpleNumber from 'components/SimpleComponents/SimpleNumber';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import localizedFormat from 'dayjs/plugin/localizedFormat';

dayjs.extend(relativeTime);
dayjs.extend(localizedFormat);

// 3. Providers / Context
import { useDocumentContext } from 'components/core/DocumentProvider';

// 4. Components
import SimpleLoadingArea from 'components/SimpleComponents/SimpleLoadingArea';
import SimpleTable from 'components/SimpleComponents/SimpleTable';

// 5. Services and stores
import useZustand from 'hooks/useZustand';
import translateX from 'utils/translateX';
import financialService from 'services/financialService';
import { useSimpleFormContext } from 'components/SimpleComponents/SimpleFormProvider';
import { openModal } from 'components/core/GenericModal';
import Blob from 'components/core/Blob';
import { removeProductSupplier } from 'modules/app-views/products/service';
import SupplierModal from './modals/SupplierModal';

// *****************************************************************************
// CONSTANTS
// *****************************************************************************

const { Text } = Typography;

// *****************************************************************************
// COMPONENT
// *****************************************************************************

const ProductsSuppliersTab = props => {
  // ---------------------------------------------------------------------------
  // CONTEXTS
  // ---------------------------------------------------------------------------

  const { data, loading, refresh, viewMode } = useDocumentContext();
  const { form } = useSimpleFormContext() || {};
  const isNewDocument = viewMode === 'add';

  // ---------------------------------------------------------------------------
  // ZUSTAND
  // ---------------------------------------------------------------------------

  const storedParameters = useZustand('app', 'parameters');

  // ---------------------------------------------------------------------------
  // STATE
  // ---------------------------------------------------------------------------

  const [expandedRowKeys, setExpandedRowKeys] = useState([]);

  // ---------------------------------------------------------------------------
  // COST SUMMARY (computed values for disabled fields)
  // ---------------------------------------------------------------------------
  const stockQty = data?.stock?.quantity || 0;
  const costPrice = data?.pricing?.costPrice || 0;
  const stockValue = financialService.roundCurrency(stockQty * costPrice);

  // Unit cost fallback: if stock = 0, use primary supplier's converted unit cost
  const defaultUnitCost = useMemo(() => {
    if (stockQty > 0 && stockValue > 0) {
      return financialService.roundCurrency(stockValue / stockQty);
    }
    const suppliers = data?.suppliers || [];
    const primary = suppliers.find(s => s.isPrimary && s.isActive !== false)
      || suppliers.find(s => s.isActive !== false)
      || suppliers[0];
    return primary?.convertedUnitCost || primary?.priceAfterChainDiscount || primary?.purchasePrice || costPrice || 0;
  }, [stockQty, stockValue, data?.suppliers, costPrice]);

  // ---------------------------------------------------------------------------
  // EFFECTS
  // ---------------------------------------------------------------------------

  // Initialize cost summary form fields from data
  useEffect(() => {
    if (!form || !data?._id) return;

    const currentUnitCost = defaultUnitCost;
    const currentPrice = data?.price || 0;
    const currentMarkup = currentUnitCost > 0
      ? financialService.calculateMarkup(currentUnitCost, currentPrice, 3)
      : (data?.pricing?.markup ?? 0);
    const currentMargin = currentUnitCost > 0
      ? financialService.calculateMargin(currentUnitCost, currentPrice, 3)
      : (data?.pricing?.margin ?? 0);

    form.setFieldValue('stockValue', stockValue);
    form.setFieldValue('stockQty', stockQty);
    form.setFieldValue(['pricing', 'costPrice'], currentUnitCost);
    form.setFieldValue(['pricing', 'markup'], currentMarkup);
    form.setFieldValue(['pricing', 'margin'], currentMargin);
    form.setFieldValue('_displayPrice', currentPrice);
  }, [data, defaultUnitCost, stockValue, stockQty, form]);

  // ---------------------------------------------------------------------------
  // HANDLERS
  // ---------------------------------------------------------------------------

  // Sync: markup changed → recalculate price + margin
  const handleMarkupChange = useCallback(value => {
    if (!form || !defaultUnitCost) return;
    const markup = value || 0;
    const newPrice = financialService.salePriceFromMarkup(defaultUnitCost, markup);
    const newMargin = financialService.calculateMargin(defaultUnitCost, newPrice, 3);
    form.setFieldValue('_displayPrice', newPrice);
    form.setFieldValue('price', newPrice); // Sync to real field (ProductCard tab1)
    form.setFieldValue(['pricing', 'margin'], newMargin);
  }, [form, defaultUnitCost]);

  // Sync: display price changed → recalculate markup + margin + sync to real price
  const handlePriceChange = useCallback(value => {
    if (!form || !defaultUnitCost) return;
    const price = value || 0;
    const newMarkup = financialService.calculateMarkup(defaultUnitCost, price, 3);
    const newMargin = financialService.calculateMargin(defaultUnitCost, price, 3);
    form.setFieldValue('price', price); // Sync to real field (ProductCard tab1)
    form.setFieldValue(['pricing', 'markup'], newMarkup);
    form.setFieldValue(['pricing', 'margin'], newMargin);
  }, [form, defaultUnitCost]);

  // Sync: margin changed → recalculate price + markup
  const handleMarginChange = useCallback(value => {
    if (!form || !defaultUnitCost) return;
    const margin = value || 0;
    const newPrice = financialService.salePriceFromMargin(defaultUnitCost, margin);
    const newMarkup = financialService.calculateMarkup(defaultUnitCost, newPrice, 3);
    form.setFieldValue('_displayPrice', newPrice);
    form.setFieldValue('price', newPrice); // Sync to real field (ProductCard tab1)
    form.setFieldValue(['pricing', 'markup'], newMarkup);
  }, [form, defaultUnitCost]);

  const handleAdd = useCallback(() => {
    openModal(SupplierModal, {
      editingSupplier: null,
      productId: data?._id,
      productSalePrice: data?.price || 0,
      productUnitUid: data?.unitUid || null,
      onSuccess: () => refresh?.(),
    });
  }, [data?._id, data?.price, data?.unitUid, refresh]);

  const handleEdit = useCallback(supplier => {
    const enriched = {
      ...supplier,
      supplierName: supplier.__entity?.name || supplier.supplierName || '',
    };
    openModal(SupplierModal, {
      editingSupplier: enriched,
      productId: data?._id,
      productSalePrice: data?.price || 0,
      productUnitUid: data?.unitUid || null,
      onSuccess: () => refresh?.(),
    });
  }, [data?._id, data?.price, data?.unitUid, refresh]);

  const handleDelete = useCallback(
    async supplier => {
      try {
        await removeProductSupplier({
          productId: data._id,
          subdocumentId: supplier._id,
        });
        refresh?.();
      } catch (_err) {
        // error handled by axiosService
      }
    },
    [data?._id, refresh]
  );

  const handleExpandedRowsChange = useCallback(keys => {
    setExpandedRowKeys(keys);
  }, []);

  // ---------------------------------------------------------------------------
  // MEMOS
  // ---------------------------------------------------------------------------

  const suppliers = useMemo(() => data?.suppliers || [], [data?.suppliers]);

  const columns = useMemo(
    () => [
      {
        title: translateX('supplier'),
        dataIndex: ['__entity'],
        sorter: (a, b) => {
          const nameA = a.__entity?.name || a.supplierName || '';
          const nameB = b.__entity?.name || b.supplierName || '';
          return nameA.localeCompare(nameB);
        },
        render: (_, record) => {
          const entity = record.__entity;
          const name = entity?.name || record.supplierName || record.supplierId || '—';
          const cpfCnpj = entity?.cpfCnpj;
          const cpfCnpjDisplay = cpfCnpj ? formatCpfCnpj(cpfCnpj) : null;

          return (
            <div>
              <span>
                <Text ellipsis strong>{name}</Text>
                {record.isPrimary && (
                  <Tag color="blue" className="ml-2">
                    {translateX('primary')}
                  </Tag>
                )}
              </span>
              {cpfCnpjDisplay && (
                <Text className="text-muted d-block">
                  {cpfCnpjDisplay}
                </Text>
              )}
            </div>
          );
        },
      },
      {
        title: translateX('cost'),
        dataIndex: 'convertedUnitCost',
        width: 110,
        align: 'right',
        sorter: (a, b) => (a.convertedUnitCost || 0) - (b.convertedUnitCost || 0),
        render: val => val ? financialService.formatCurrency(val) : '—',
      },
      {
        title: translateX('margin'),
        dataIndex: 'markup',
        width: 100,
        align: 'right',
        sorter: (a, b) => (a.markup || 0) - (b.markup || 0),
        render: (_, record) => {
          const cost = record.convertedUnitCost || record.priceAfterChainDiscount || record.purchasePrice || 0;
          const price = data?.price || 0;
          const margin = cost > 0 ? financialService.calculateMargin(cost, price, 2) : 0;
          return <Text strong>{financialService.formatPercentage(margin,3)}</Text>;
        },
      },
      {
        title: translateX('last_update'),
        dataIndex: 'updatedAt',
        width: 160,
        className: 'text-nowrap text-transform-content-none',
        sorter: (a, b) => {
          const dateA = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
          const dateB = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
          return dateA - dateB;
        },
        render: value => {
          if (!value) return null;
          return <Tooltip title={dayjs(value).format('L LTS')}>{dayjs(value).fromNow()}</Tooltip>;
        },
      },
      {
        title: translateX('actions'),
        dataIndex: 'supplierId',
        width: 80,
        align: 'center',
        render: (_, record) => (
          <div className="suppliers-actions-cell">
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
            <Popconfirm title={translateX('are_you_sure')} onConfirm={() => handleDelete(record)}>
              <Button type="text" size="small" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          </div>
        ),
      },
    ],
    [handleEdit, handleDelete, data?.price]
  );

  const expandedRowRender = useCallback(
    record => (
      <div className="square suppliers-bg-container">
        {/* Block 1: Product Identification (3 cols, all rows aligned) */}
        <Descriptions size="small" column={{ xs: 1, sm: 3 }} bordered>
          {/* Row 1: SKU | Description | Purchase Unit */}
          <Descriptions.Item label={translateX('supplier_sku')}>
            {record.sku || '—'}
          </Descriptions.Item>
          <Descriptions.Item label={translateX('description')}>
            {record.description || '—'}
          </Descriptions.Item>
          <Descriptions.Item label={translateX('purchase_unit')}>
            {resolveUnitName(storedParameters, record.unitUid)}
          </Descriptions.Item>

          {/* Row 2: CFOP | Sit. Trib. | Warranty */}
          <Descriptions.Item label="CFOP">
            {record.cfop || '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Sit. Trib.">
            {record.taxSituation || '—'}
          </Descriptions.Item>
          <Descriptions.Item label={translateX('warranty')}>
            {record.warrantyDays != null ? `${record.warrantyDays} ${translateX('days')}` : '—'}
          </Descriptions.Item>

          {/* Row 3: Delivery | Notes (span 2) */}
          <Descriptions.Item label={translateX('delivery_time')}>
            {record.leadTimeDays != null ? `${record.leadTimeDays} ${translateX('days')}` : '—'}
          </Descriptions.Item>
          <Descriptions.Item label={translateX('observation')} span={2}>
            {record.notes || '—'}
          </Descriptions.Item>
        </Descriptions>

        {/* Block 2: Cost Composition & Margin */}
        <Divider className="suppliers-cost-divider">
          <Text type="secondary" className="suppliers-cost-label">
            <DollarOutlined className="mr-1" />
            {translateX('cost_composition')}
          </Text>
        </Divider>
        <Descriptions size="small" column={{ xs: 1, sm: 3 }} bordered>
          {/* Row 1: List Price | Discount | Purchase Price */}
          <Descriptions.Item label={translateX('list_price')}>
            {formatCurrency(record.listPrice)}
          </Descriptions.Item>
          <Descriptions.Item label={translateX('chained_discount')}>
            {formatChainDiscount(record.chainDiscount)}
          </Descriptions.Item>
          <Descriptions.Item label={translateX('purchase_price')}>
            {formatCurrency(record.purchasePrice)}
          </Descriptions.Item>

          {/* Row 2: Applied Costs (single cell spanning 3 columns) */}
          <Descriptions.Item label={translateX('applied_costs')} span={3}>
            {record.appliedCosts?.length > 0 ? (
              record.appliedCosts.map((cost, idx) => (
                <div key={idx} className="suppliers-applied-cost-row">
                  <strong className='mr-2'>{cost.name}</strong>

                  <Tag
                    color={getCostTypeColor(cost.type)}
                    className="suppliers-cost-tag-sm"
                  >
                    {getCostTypeLabel(cost.type)}
                  </Tag>
                  <Blob />
                  {cost.mode === 'percentage'
                    ? `${financialService.formatNumber(cost.percentage, { decimals: 3 })}%`
                    : formatCurrency(cost.value)}
                  <Blob />
                  <span className="text-muted">{getPayerSourceLabel(cost.payerSource)}</span>
                </div>
              ))
            ) : '—'}
          </Descriptions.Item>

          {/* Row 3: Replacement Cost | NF Discount | NF Price */}
          <Descriptions.Item label={translateX('replacement_cost')}>
            {formatCurrency(record.priceAfterChainDiscount)}
          </Descriptions.Item>
          <Descriptions.Item label={translateX('last_nf_discount')}>
            {record.lastNfDiscountPercentage != null
              ? `${financialService.formatNumber(record.lastNfDiscountPercentage, { decimals: 2 })}%`
              : '—'}
          </Descriptions.Item>
          <Descriptions.Item label={translateX('final_price')}>
            {formatCurrency(record.priceAfterAllDiscounts)}
          </Descriptions.Item>

          {/* Row 4: Conversion Factor | Converted Value | Markup */}
          <Descriptions.Item label={translateX('conversion_factor')}>
            {record.conversionFactor || 1}
          </Descriptions.Item>
          <Descriptions.Item label={translateX('converted_value')}>
            {formatCurrency(record.convertedUnitCost)}
          </Descriptions.Item>
          <Descriptions.Item label="Markup">
            {record.markup != null
              ? `${financialService.formatNumber(record.markup, { decimals: 3 })}%`
              : '—'}
          </Descriptions.Item>
        </Descriptions>
      </div>
    ),
    [storedParameters]
  );

  // ---------------------------------------------------------------------------
  // UI
  // ---------------------------------------------------------------------------

  return (
    <div className="container suppliers-tab-wrapper">
      <SimpleLoadingArea name="productSuppliers" isFetching={loading} data={data} />

      {/* Cost Summary Card — hidden for new documents to avoid duplicate form fields */}
      {!isNewDocument && <Card
        title={<span className="text-muted">{translateX('cost_summary')}</span>}
        className="mb-3"
      >
        <Row gutter={[16, 0]}>
          <Col xs={24} sm={12} md={8} lg={5}>
            <SimpleCurrency name="stockValue" label="stock_value" disabled />
          </Col>
          <Col xs={24} sm={12} md={8} lg={3}>
            <SimpleNumber name="stockQty" label="stock_qty" disabled />
          </Col>
          <Col xs={24} sm={12} md={8} lg={4}>
            <SimpleCurrency name={['pricing', 'costPrice']} label="unit_stock_cost" disabled />
          </Col>
          <Col xs={24} sm={12} md={8} lg={4}>
            <SimpleNumber
              name={['pricing', 'markup']}
              label="markup"
              decimalPlaces={3}
              max={99999}
              percentage
              onChange={handleMarkupChange}
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={4}>
            <SimpleCurrency name="_displayPrice" label="sale_price" onChange={handlePriceChange} />
          </Col>
          <Col xs={24} sm={12} md={8} lg={4}>
            <SimpleNumber
              name={['pricing', 'margin']}
              label="margin"
              decimalPlaces={3}
              max={99.999}
              percentage
              onChange={handleMarginChange}
            />
          </Col>
        </Row>
      </Card>}

      <Row justify="end" className="mb-2">
        <Button
          type="default"
          size="small"
          icon={<PlusOutlined />}
          onClick={e => {
            e.stopPropagation();
            handleAdd();
          }}
        />
      </Row>

      <SimpleTable
        columns={columns}
        dataSource={suppliers}
        rowKey="supplierId"
        pagination={false}
        size="small"
        scroll={{ x: 'max-content' }}
        expandable={{
          expandedRowKeys,
          onExpandedRowsChange: handleExpandedRowsChange,
          expandedRowRender,
        }}
      />
    </div>
  );
};

// *****************************************************************************
// PROPTYPES
// *****************************************************************************

// *****************************************************************************
// EXPORT
// *****************************************************************************

export default ProductsSuppliersTab;

// *****************************************************************************
// HELPERS
// *****************************************************************************

function formatCurrency(value) {
  if (value == null || value === '') return '—';
  const num = Number(value);
  if (isNaN(num)) return '—';
  return num.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function formatChainDiscount(chainDiscount) {
  if (!chainDiscount) return '—';
  if (typeof chainDiscount === 'string') return chainDiscount;
  if (chainDiscount.chain) {
    return `${chainDiscount.chain} (${chainDiscount.totalPercentage}%)`;
  }
  return '—';
}

function resolveUnitName(storedParameters, uid) {
  if (!uid) return '—';
  const units = storedParameters?.modules?.products?.units;
  if (!Array.isArray(units)) return uid;
  const found = units.find(u => u.uid === uid);
  return found?.name || '—';
}

function getCostTypeColor(type) {
  const colors = { informative: 'default', cost: 'blue', purchaseTax: 'orange', saleTax: 'red' };
  return colors[type] || 'default';
}

function getCostTypeLabel(type) {
  const labels = { informative: 'info', cost: 'custo', purchaseTax: 'imp.compra', saleTax: 'imp.venda' };
  return labels[type] || type;
}

function getPayerSourceLabel(source) {
  const labels = {
    municipal: translateX('payer_source_municipal'),
    state: translateX('payer_source_state'),
    federal: translateX('payer_source_federal'),
    other: translateX('payer_source_other'),
  };
  return labels[source] || source || '';
}

function formatCpfCnpj(value) {
  if (!value) return null;
  const digits = String(value).replace(/\D/g, '');
  if (digits.length === 14) {
    return `CNPJ: ${digits.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5')}`;
  }
  if (digits.length === 11) {
    return `CPF: ${digits.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4')}`;
  }
  return value;
}
